/**
 * PayrollSearch — Consulta histórica de nóminas por CUIL o nombre.
 *
 * Objetivo: ante un reclamo judicial o interno, poder ubicar rápidamente TODAS
 * las OPs donde un empleado aparece como beneficiario. Muestra el detalle
 * completo por OP con posibilidad de descargar el PDF de la OP y el .txt original.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Search, FileText, Download, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(n || 0));
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('es-AR') : '—';

export default function PayrollSearch({ embedded = false }) {
  const [cuil, setCuil] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const buscar = async () => {
    if (!cuil.trim() && !name.trim()) { toast.error('Ingresá un CUIL o un nombre'); return; }
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (cuil.trim()) p.append('cuil', cuil.trim());
      if (name.trim()) p.append('name', name.trim());
      const res = await axios.get(`${BACKEND_URL}/api/payroll/search?${p}`, { withCredentials: true });
      setData(res.data);
      if ((res.data?.results || []).length === 0) toast.info('Sin resultados');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en la búsqueda');
    } finally { setLoading(false); }
  };

  return (
    <div className={embedded ? 'space-y-4' : 'p-6 space-y-4'} data-testid="payroll-search-page">
      {!embedded && (
        <div className="flex items-center gap-3">
          <Users className="w-7 h-7 text-violet-600" />
          <div>
            <h1 className="text-2xl font-bold">Consulta de Nómina</h1>
            <p className="text-sm text-muted-foreground">Buscá todas las OPs donde un empleado apareció como beneficiario (útil para requerimientos judiciales).</p>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>CUIL</Label>
              <Input value={cuil} onChange={(e) => setCuil(e.target.value)} placeholder="11 dígitos, sin guiones"
                     onKeyDown={(e) => e.key === 'Enter' && buscar()} data-testid="payroll-search-cuil" />
            </div>
            <div>
              <Label>Nombre (contiene)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. GARCIA o MARIA"
                     onKeyDown={(e) => e.key === 'Enter' && buscar()} data-testid="payroll-search-name" />
            </div>
            <div className="flex items-end">
              <Button onClick={buscar} disabled={loading} className="w-full bg-violet-600 hover:bg-violet-700" data-testid="payroll-search-btn">
                <Search className="w-4 h-4 mr-1" /> {loading ? 'Buscando…' : 'Buscar'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {data && (
        <>
          {data.employees?.length > 1 && (
            <Card>
              <CardHeader><CardTitle className="text-lg">Empleados encontrados ({data.employees.length})</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.employees.map(e => (
                  <button key={e.cuil} onClick={() => { setCuil(e.cuil); setName(''); }} className="text-left rounded-md border p-2 hover:bg-slate-50">
                    <div className="font-medium">{e.beneficiary_name}</div>
                    <div className="text-xs text-muted-foreground font-mono">CUIL {e.cuil} · {e.count} OP · Total {fmt(e.total)}</div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>Resultados ({data.total_records})</span>
                <Badge className="bg-violet-100 text-violet-800">Detalle por OP</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Nº OP</TableHead>
                  <TableHead>CUIL</TableHead>
                  <TableHead>Beneficiario</TableHead>
                  <TableHead className="text-right">Importe</TableHead>
                  <TableHead className="text-right">Total OP</TableHead>
                  <TableHead className="text-center w-32">Documentos</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(data.results || []).length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">Sin resultados</TableCell></TableRow>
                  )}
                  {(data.results || []).map((r, i) => (
                    <TableRow key={i} data-testid={`payroll-result-row-${i}`}>
                      <TableCell>{fmtDate(r.payment_order_date)}</TableCell>
                      <TableCell className="font-medium">{r.payment_order_number}</TableCell>
                      <TableCell className="font-mono text-xs">{r.cuil}</TableCell>
                      <TableCell>{r.beneficiary_name}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">{fmt(r.amount)}</TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">{fmt(r.payment_order_total)}</TableCell>
                      <TableCell className="text-center">
                        <Button variant="ghost" size="icon" title="PDF de la OP" onClick={() => window.open(`${BACKEND_URL}/api/treasury/payment-orders/${r.payment_order_id}/pdf?t=${Date.now()}`, '_blank')}>
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Descargar .txt original del homebanking" onClick={() => window.open(`${BACKEND_URL}/api/payroll/attachment/${r.payment_order_id}?t=${Date.now()}`, '_blank')}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
